import { useMemo, useState } from "react";
import { useGetCyclesQuery } from "@/store/api";
import { useAppSelector } from "@/store";
import MyCycles from "@/routes/MyCycles";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StalenessBadge } from "@/components/domain/StalenessBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { StatusPill } from "@/components/domain/StatusPill";
import { StepTrace } from "@/components/domain/StepTrace";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { fmtUsd, fmtTime } from "@/lib/format";
import { HourlyOpenPositions } from "@/components/domain/HourlyOpenPositions";

const STATUSES = ["all", "ok", "error", "paused", "running"] as const;
type StatusFilter = (typeof STATUSES)[number];

export default function Cycles() {
  const role = useAppSelector((s) => s.auth.user?.role);
  if (role !== "admin") return <MyCycles />;
  return <AdminCycles />;
}

function AdminCycles() {
  const { data, isLoading } = useGetCyclesQuery(undefined, { pollingInterval: 30000 });
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [since, setSince] = useState("");
  const [activeId, setActiveId] = useState<number | null>(null);

  const rows = useMemo(() => {
    let r = data ?? [];
    if (filter !== "all") r = r.filter((c) => c.status === filter);
    if (since) {
      const ms = new Date(since).getTime();
      if (!Number.isNaN(ms)) {
        r = r.filter((c) => new Date(c.started_at).getTime() >= ms);
      }
    }
    return r;
  }, [data, filter, since]);

  return (
    <div className="space-y-4">
      <HourlyOpenPositions pollMs={15000} />
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <span>Hourly cycles</span>
              <StalenessBadge
                ts={(data ?? [])[0]?.started_at}
                warnHours={1.5} staleHours={3} veryStaleHours={12}
                label="last cycle"
              />
            </CardTitle>
            <p className="text-xs text-muted mt-1">
              Click any row to open its full step trace.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={
                  "px-2.5 h-7 rounded-md text-xs border transition-colors " +
                  (filter === s
                    ? "bg-bg text-fg border-border"
                    : "text-muted border-transparent hover:text-fg hover:bg-bg")
                }
              >
                {s}
              </button>
            ))}
            <Input
              type="datetime-local"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="max-w-[200px] h-8 text-xs"
              title="Since"
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted">No cycles match the filter.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>id</TH>
                  <TH>Started</TH>
                  <TH>Finished</TH>
                  <TH>Status</TH>
                  <TH>Trade</TH>
                  <TH>Owner</TH>
                  <TH className="text-right">Free quote</TH>
                  <TH className="text-right">Position value</TH>
                  <TH>Notes</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((c) => (
                  <TR
                    key={c.id}
                    onClick={() => setActiveId(c.id)}
                    className="cursor-pointer"
                  >
                    <TD>
                      <Badge tone="info">#{c.id}</Badge>
                    </TD>
                    <TD className="text-xs text-muted">{fmtTime(c.started_at)}</TD>
                    <TD className="text-xs text-muted">{fmtTime(c.finished_at ?? null)}</TD>
                    <TD><StatusPill status={c.status} /></TD>
                    <TD className="text-xs whitespace-nowrap">
                      {c.decision_action === "BUY" || c.decision_action === "SELL" ? (
                        <span>
                          <span className={
                            "font-semibold mr-1 " +
                            (c.decision_action === "BUY" ? "text-good" : "text-bad")
                          }>{c.decision_action}</span>
                          <span className="text-fg">{c.decision_symbol ?? "—"}</span>
                          {c.decision_size_usd != null && (
                            <span className="text-muted ml-1">
                              · {fmtUsd(c.decision_size_usd)}
                            </span>
                          )}
                        </span>
                      ) : c.decision_action === "HOLD" ? (
                        <span className="text-muted">HOLD</span>
                      ) : (
                        <span className="text-muted/60">—</span>
                      )}
                    </TD>
                    <TD className="text-xs">
                      {c.user_id != null ? (
                        <span title={c.user_email ?? ""}>
                          <span className="text-muted">#{c.user_id}</span>{" "}
                          <span className="text-fg">{c.user_email ?? ""}</span>
                        </span>
                      ) : (
                        <span className="text-muted/60">global</span>
                      )}
                    </TD>
                    <TD className="text-right tabular">{fmtUsd(c.quote_free ?? null)}</TD>
                    <TD className="text-right tabular">{fmtUsd(c.position_value_usd ?? null)}</TD>
                    <TD className="text-xs text-muted truncate max-w-xs">{c.notes ?? ""}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <StepTrace
        open={activeId != null}
        onClose={() => setActiveId(null)}
        cycleId={activeId}
      />
    </div>
  );
}
